/**
 * Format a number as currency
 */
export function formatCurrency(
  amount: number,
  currency: string = 'AUD',
  decimals: number = 0
): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount)
}

/**
 * Format a number with K/M/B suffixes
 */
export function formatNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B'
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
  }
  return num.toString()
}

/**
 * Format a number as percentage
 */
export function formatPercent(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`
}

/**
 * Parse a formatted number string back to number
 */
export function parseNumber(str: string): number {
  const cleaned = str.replace(/[^0-9.-]/g, '')
  return parseFloat(cleaned) || 0
}

/**
 * Parse a date string to a Date, treating SQLite's space-separated
 * 'YYYY-MM-DD HH:MM:SS' form as UTC. D1's datetime('now') emits that form with no
 * timezone marker, and `new Date()` would otherwise interpret it as *local* time —
 * skewing every derived value (relative times, dates) by the local UTC offset. ISO
 * strings (with 'T' and/or a 'Z'/offset) are passed straight through.
 */
export function parseDbDate(dateStr: string): Date {
  // SQLite UTC form: date + space + time, no timezone designator → normalise to UTC.
  const m = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(dateStr)
  if (m) return new Date(`${m[1]}T${m[2]}Z`)
  return new Date(dateStr)
}

/**
 * Format a date string
 */
export function formatDate(dateStr: string): string {
  const date = parseDbDate(dateStr)
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Format a date with time
 */
export function formatDateTime(dateStr: string): string {
  const date = parseDbDate(dateStr)
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Get relative time (e.g., "2 hours ago")
 */
export function getRelativeTime(dateStr: string): string {
  const date = parseDbDate(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

  return formatDate(dateStr)
}
